import { isToday } from 'date-fns';
import moment from 'moment/moment';
import { useState } from 'react';

import { useData } from '@shared/api/hooks/useData';
import { deleteTimeTrackingEntry } from '@shared/time-tracking-entries/endpoints/deleteTimeTrackingEntry';
import { endTimeTrackingEntryById } from '@shared/time-tracking-entries/endpoints/endTimeTrackingEntryById';
import { getTimeTrackingEntries } from '@shared/time-tracking-entries/endpoints/getTimeTrackingEntries';
import { useFetchTimeTrackingGroup } from '@shared/time-tracking-groups/hooks/useFetchTimeTrackingGroup';
import { Button } from '@shared/ui/components/Button';
import { CrewMemberCell } from '@shared/ui/components/CrewMemberCell';
import { DataTable } from '@shared/ui/components/DataTable';
import { Icon } from '@shared/ui/components/Icon';
import { Modal, ModalContent } from '@shared/ui/components/Modal';
import { Portlet } from '@shared/ui/components/Portlet';
import { Tooltip } from '@shared/ui/components/Tooltip';

import { AdminWrapper } from '@admin/layout/components/AdminWrapper';
import BarcodeTimeTrackingPortlet from '@admin/time-tracking/components/BarcodeTimeTrackingPortlet/BarcodeTimeTrackingPortlet';
import CrewMemberTimeTrackingPortlet from '@admin/time-tracking/components/CrewMemberTimeTrackingPortlet/CrewMemberTimeTrackingPortlet';
import { EditTimeTrackingEntryForm } from '@admin/time-tracking/components/EditTimeTrackingEntryForm';
import Timer from '@admin/time-tracking/views/ListTimeTrackingEntries/Timer';
import TimerIcon from '@admin/time-tracking/views/ListTimeTrackingEntries/TimerIcon';

import ExportTimeTrackingEntriesButton from './ExportTimeTrackingEntriesButton';

type TimeTrackingEntry = App.Domain.TimeTracking.Data.TimeTrackingEntryData;

export function ListTimeTrackingEntries({ match }: { match: any }) {
  const [edittingTimeTrackingEntryId, setEdittingTimeTrackingEntryId] = useState<number | null>(null);

  const timeTrackingGroupId = match.params.id;
  const timeTrackingGroup = useFetchTimeTrackingGroup(timeTrackingGroupId);

  const timeTrackingEntries = useData<TimeTrackingEntry>({
    key: 'time-tracking-entries',
    get: getTimeTrackingEntries,
    query: {
      filter: { time_tracking_group_id: timeTrackingGroupId },
      include: ['crewMember.user', 'crewMember.registration.company'],
    },
    defaults: { sort: '-updated_at' },
  });

  function end(timeTrackingEntry: TimeTrackingEntry) {
    endTimeTrackingEntryById({ id: timeTrackingEntry.id }).then(() => {
      timeTrackingEntries.refresh();
    });
  }

  if (!timeTrackingGroup) {
    return null;
  }

  return (
    <AdminWrapper
      title={`Time Tracking: ${timeTrackingGroup?.name}`}
      breadcrumb={{
        label: 'Time Tracking Groups',
        url: '/admin/time-tracking-groups',
      }}
      innerClassName="space-y-8"
    >
      <div className="flex flex-row flex-wrap md:flex-nowrap gap-8">
        <BarcodeTimeTrackingPortlet
          className="w-full md:w-1/2 md:max-w-[40ch]"
          timeTrackingGroupId={timeTrackingGroupId}
          onStartOrEnd={() => timeTrackingEntries.refresh()}
        />

        <CrewMemberTimeTrackingPortlet
          className="w-full md:w-1/2 md:max-w-[40ch]"
          timeTrackingGroupId={timeTrackingGroupId}
          onStartOrEnd={() => timeTrackingEntries.refresh()}
        />
      </div>

      <Portlet actions={[<ExportTimeTrackingEntriesButton groupId={timeTrackingGroup.id} />]}>
        <DataTable<TimeTrackingEntry>
          data={timeTrackingEntries}
          columns={[
            {
              name: 'crewMember.name',
              filterable: {
                label: 'Crew Member',
                icon: 'user',
                name: 'user_name',
              },
              render: (row) => <CrewMemberCell crewMember={row.crewMember!} showCompany />,
            },
            {
              name: 'startedAt',
              label: 'Start/end',
              type: 'number',
              render: (row) => {
                const formatDate = (dateString: string | null) => {
                  if (!dateString) return null;
                  const date = moment.utc(dateString).local();
                  const formatStr = isToday(date.toDate()) ? 'HH:mm' : 'DD/MM HH:mm';
                  return date.format(formatStr);
                };

                return (
                  <>
                    <p className="font-bold">{formatDate(row.startedAt)}</p>
                    {row.endedAt && <p>{formatDate(row.endedAt)}</p>}
                  </>
                );
              },
              filterable: {
                name: 'started_at_day',
                label: 'Day started',
                type: 'date',
                icon: 'calendar',
              },
            },
            {
              name: 'duration',
              label: 'Duration',
              type: 'number',
              render: (row) => (
                <>
                  {row.endedAt && row.endedAtDayEnd && (
                    <Tooltip content="Automatically ended at the end of the festival day">
                      <Icon icon="warning" className="mr-2" />
                    </Tooltip>
                  )}
                  <Timer startedAt={row.startedAt} endedAt={row.endedAt ? row.endedAt : null} />
                </>
              ),
            },
            {
              type: 'actions',
              render: (row) =>
                row.endedAt ? null : (
                  <Button size="sm" onClick={() => end(row)}>
                    <TimerIcon /> Stop
                  </Button>
                ),
            },
          ]}
          filters={[
            {
              label: 'Company',
              name: 'registration_id',
              type: 'select',
              options: timeTrackingGroup.registrations!.map((registration) => ({
                value: registration.id,
                label: registration.company!.name,
              })),
            },
            {
              label: 'Active',
              name: 'active',
              type: 'select',
              options: [
                { value: 1, label: 'Active' },
                { value: 0, label: 'Stopped' },
              ],
            },
          ]}
          onDelete={deleteTimeTrackingEntry}
          onEdit={(row) => setEdittingTimeTrackingEntryId(row.id)}
        />

        <Modal open={!!edittingTimeTrackingEntryId} onClose={() => setEdittingTimeTrackingEntryId(null)}>
          <ModalContent title="Update Entry" size="2xl" withoutAutoFocus>
            {({ close }) => (
              <EditTimeTrackingEntryForm
                timeTrackingEntryId={edittingTimeTrackingEntryId!}
                onSave={(timeTrackingEntry: TimeTrackingEntry) => {
                  timeTrackingEntries.refresh();

                  close();
                }}
                onCancel={close}
              />
            )}
          </ModalContent>
        </Modal>
      </Portlet>
    </AdminWrapper>
  );
}
